import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import tw from 'twin.macro';

import { Modal } from '@components/Modal';
import { Button } from '@components/Button';
import { Loader } from '@components/Loader';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { CloseIcon } from '@icons/CloseIcon';
import { useIntegrationTags } from '@insights_module/hooks/useIntegrationTags';
import { IntegrationType } from '@insights_module/types/Integrations';
import { updateIntegrationTagDescription } from '@insights_module/services/megaphone';
import {
  INTEGRATION_QUERY_KEYS,
  INTEGRATION_TYPE_MAPPER,
} from '@insights_module/constants/integrations';
import { useNotification } from '@insights_module/hooks/useNotification';

const Container = tw.div`w-full max-w-[675px] md:min-w-[675px] rounded-lg overflow-hidden shadow-modal mx-auto`;
const Header = tw.div`w-full px-[24px] py-[12px] flex items-center justify-between bg-neutral-background border-b border-solid border-gray-1 font-semibold text-[16px] text-text-and-icon`;
const Body = tw.div`w-full bg-neutral-background p-[24px]`;
const Footer = tw.div`w-full px-[24px] py-[12px] flex items-center justify-end gap-[8px] bg-neutral-background border-t border-solid border-gray-1`;
const TextArea = tw.textarea`w-full p-[8px] flex items-center justify-end gap-[8px] rounded-lg border border-solid border-gray-1 bg-neutral-background text-text-and-icon`;
const Form = tw.form`block w-full`;

type EditTagDescriptionFormType = {
  description: string;
};

export const EditTagDescriptionModal = ({ type }: { type: IntegrationType }) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register, handleSubmit, reset } = useForm<EditTagDescriptionFormType>();
  const { notify, NTFC_TYPE } = useNotification();
  const { isDescriptionModalOpen, setIsDescriptionModalOpen, tagToEdit, searchTerm } =
    useIntegrationsContext();
  const { getTagById } = useIntegrationTags(type, searchTerm);
  const tag = getTagById(tagToEdit);

  const defaultValue = useMemo(() => tag?.query.description, [tag?.query.description]);

  const handleCancel = () => {
    setIsDescriptionModalOpen(false);
    reset();
  };

  const onSubmit = (formData: EditTagDescriptionFormType) => {
    if (!tag?.query?.id) return;

    setIsLoading(true);
    updateIntegrationTagDescription(tag?.query?.id, formData)
      .then(() =>
        queryClient.invalidateQueries([
          INTEGRATION_QUERY_KEYS.INFINITE_TAGS,
          INTEGRATION_TYPE_MAPPER[type],
        ])
      )
      .catch(() =>
        notify({
          type: NTFC_TYPE.WARNING,
          message: 'Error while updating tag description.',
          duration: 2000,
        })
      )
      .finally(() => {
        setIsLoading(false);
        setIsDescriptionModalOpen(false);
        reset();
      });
  };

  if (!isDescriptionModalOpen) return null;

  return (
    <Modal onClose={handleCancel} overlayStyles={tw`bg-muted-transparent`}>
      <Container>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Header>
            Change Description
            <button onClick={handleCancel} tw='cursor-pointer'>
              <CloseIcon tw='fill-text-and-icon' stroke='none' />
            </button>
          </Header>
          <Body>
            <TextArea defaultValue={defaultValue} {...register('description')} />
          </Body>
          <Footer>
            <Button.Secondary tw='text-muted' onClick={handleCancel}>
              Cancel
            </Button.Secondary>
            <Button.Primary type='submit' tw='[&>div]:border-white! [&>div]:border-b-transparent!'>
              {isLoading ? <Loader theme='dark' size='xs' /> : 'Save Edits'}
            </Button.Primary>
          </Footer>
        </Form>
      </Container>
    </Modal>
  );
};
